import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  type FirmSignature,
  createFirmSignature,
  deleteFirmSignature,
  getFirmSettings,
  listFirmSignatures,
  updateFirmSettings,
  updateFirmSignature,
} from "@/features/settings/firm-api";
import { useAuthedImage } from "@/hooks/use-authed-image";
import { ApiError } from "@/lib/api";

function errMessage(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback;
}

/** Signatures live in private media, so the preview loads through the authed,
 * firm-scoped endpoint (not the raw media path) — mirrors the Settings →
 * Signatures tab. Its own component so the hook isn't called inside a map. */
function SignaturePreview({ sig }: { sig: FirmSignature }) {
  const src = useAuthedImage(
    sig.imageUrl ? `/firm/signatures/${sig.id}/image/?v=${sig.id}` : null,
  );
  if (!sig.imageUrl) return null;
  return src ? (
    <img
      src={src}
      alt={`${sig.name} preview`}
      style={{ width: `${sig.scale}%` }}
      className="max-h-20 object-contain"
    />
  ) : (
    <span className="text-muted-foreground text-xs">Loading…</span>
  );
}

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Resize firm logo (legacy `resize_logo.php`): a scale-% slider over a live
 * preview. Persists the stored scale to firm settings; the PDF render applies it
 * to generated continuation/attachment pages. The logo image itself is uploaded
 * in Settings → Company.
 */
export function ResizeLogoDialog({ open, onOpenChange }: DialogProps) {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [scale, setScale] = useState(100);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    getFirmSettings()
      .then((s) => {
        setLogoUrl(s.logoUrl);
        setScale(s.logoScale);
      })
      .catch(() => toast.error("Couldn't load firm branding."));
  }, [open]);

  async function handleSave() {
    setSaving(true);
    try {
      await updateFirmSettings({ logoScale: scale });
      toast.success("Logo size saved");
      onOpenChange(false);
    } catch (err) {
      toast.error(errMessage(err, "Couldn't save the logo size."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resize firm logo</DialogTitle>
          <DialogDescription>
            Applied to generated attachment pages. Upload the logo image in
            Settings → Company.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6">
          <div className="bg-muted flex min-h-32 items-center justify-center rounded-lg p-4">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Firm logo preview"
                style={{ width: `${scale}%` }}
                className="max-h-40 object-contain"
              />
            ) : (
              <p className="text-muted-foreground text-sm">
                No logo uploaded yet — add one in Settings → Company.
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <Label>Scale — {scale}%</Label>
            <Slider
              min={1}
              max={100}
              step={1}
              value={[scale]}
              onValueChange={(vals) => setScale(vals[0] ?? scale)}
              disabled={!logoUrl}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !logoUrl}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Manage / resize signatures (legacy `add_signature.php` + `resize_signature.php`):
 * add a named signature image, adjust each signature's scale %, pick the default
 * (auto-applied at generate), or delete one. Placement is a server-side overlay.
 */
export function SignatureDialog({ open, onOpenChange }: DialogProps) {
  const [signatures, setSignatures] = useState<FirmSignature[]>([]);
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function reload() {
    listFirmSignatures()
      .then(setSignatures)
      .catch(() => toast.error("Couldn't load signatures."));
  }

  useEffect(() => {
    if (open) reload();
  }, [open]);

  async function handleAdd() {
    if (!name.trim() || !file) return;
    setBusy(true);
    try {
      await createFirmSignature({
        name: name.trim(),
        file,
        scale: 100,
        isDefault: signatures.length === 0,
      });
      setName("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      reload();
      toast.success("Signature added");
    } catch (err) {
      toast.error(errMessage(err, "Couldn't add the signature."));
    } finally {
      setBusy(false);
    }
  }

  async function patch(id: string, change: { scale?: number; isDefault?: boolean }) {
    try {
      await updateFirmSignature(id, change);
      reload();
    } catch (err) {
      toast.error(errMessage(err, "Couldn't update the signature."));
    }
  }

  async function remove(id: string) {
    try {
      await deleteFirmSignature(id);
      reload();
    } catch (err) {
      toast.error(errMessage(err, "Couldn't delete the signature."));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage signatures</DialogTitle>
          <DialogDescription>
            Named signature images overlaid on generated PDFs. The default is
            applied automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {signatures.length === 0 && (
            <p className="text-muted-foreground text-sm">No signatures yet.</p>
          )}
          {signatures.map((sig) => (
            <div key={sig.id} className="flex flex-col gap-2 rounded-lg border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{sig.name}</span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={sig.isDefault ? "default" : "outline"}
                    onClick={() => patch(sig.id, { isDefault: true })}
                    disabled={sig.isDefault}
                  >
                    {sig.isDefault ? "Default" : "Set default"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(sig.id)}>
                    Delete
                  </Button>
                </div>
              </div>
              <SignaturePreview sig={sig} />
              <Label className="text-xs">Scale — {sig.scale}%</Label>
              <Slider
                min={1}
                max={100}
                step={1}
                value={[sig.scale]}
                onValueCommit={(vals) => patch(sig.id, { scale: vals[0] ?? sig.scale })}
                onValueChange={(vals) =>
                  setSignatures((prev) =>
                    prev.map((s) =>
                      s.id === sig.id ? { ...s, scale: vals[0] ?? s.scale } : s,
                    ),
                  )
                }
              />
            </div>
          ))}

          <div className="flex flex-col gap-3 rounded-lg border border-dashed p-3">
            <p className="text-sm font-medium">Add a signature</p>
            <div className="grid gap-2">
              <Label htmlFor="sig-name">Name</Label>
              <Input
                id="sig-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Managing Partner"
              />
            </div>
            <Input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Button onClick={handleAdd} disabled={busy || !name.trim() || !file}>
              {busy ? "Adding…" : "Add signature"}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
